import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ClaimKeys } from '../constants';

@Injectable({
  providedIn: 'root',
})
export class AuthStateService {
  private readonly _authData = new BehaviorSubject<Record<string, unknown> | null>(null);

  // Observable for components to subscribe to
  readonly authData$: Observable<Record<string, unknown> | null> = this._authData.asObservable();

  // Store authData
  setAuthData(data: Record<string, unknown> | null): void {
    this._authData.next(data);
  }

  // Retrieve current value (synchronously)
  getAuthData(): Record<string, unknown> | null {
    return this._authData.value;
  }

  getRole(): string | null {
    const role: unknown = this.getAuthData()?.[ClaimKeys.roles];
    if (typeof role === 'string') return role;
    if (Array.isArray(role) && role.every((value: unknown): boolean => typeof value === 'string')) {
      return role.join(',');
    }
    return null;
  }

  /** Matches the Admin policy's exact comma-separated role values. */
  hasAdminRole(): boolean {
    return this.getRole()?.split(',').includes('Admin') ?? false;
  }

  /** Matches the LabAdmin role without granting the broader Admin role. */
  hasLabAdminRole(): boolean {
    return this.getRole()?.split(',').includes('LabAdmin') ?? false;
  }

  /** Matches users holding either administration role, Admin or LabAdmin. */
  hasAdministrationRole(): boolean {
    return this.hasAdminRole() || this.hasLabAdminRole();
  }

  hasDoctorRole(): boolean {
    return this.getRole()?.toLowerCase()?.includes('doctor') ?? false;
  }

  hasLabTechnicianRole(): boolean {
    return this.getRole()?.toLowerCase()?.includes('labtechnician') ?? false;
  }

  isDoctor(): boolean {
    return this.getRole()?.toLowerCase() === 'doctor';
  }

  isLabTechnician(): boolean {
    return this.getRole()?.toLowerCase() === 'labtechnician';
  }
  officeId(): string | null {
    const officeId: unknown = this.getAuthData()?.[ClaimKeys.officeId];
    return typeof officeId === 'string' ? officeId : null;
  }
}
